/** AutoCAD's arc and circle FLYOUTS — thirteen ways to say the same two entities.
 *
 * ⚠⚠ **THESE ARE NOT THIRTEEN TOOLS. THEY ARE ONE ARC AND ONE CIRCLE, ASKED FOR DIFFERENTLY.**
 * That is the whole reason this file is small: every given is reduced to the canonical form the
 * server already accepts — a circle as `[centre, a point on it]`, an arc as `[start, a point the
 * arc passes through, end]` — and `edits.do_draw` never learns that a flyout exists.
 *
 * ⚠ Which also means the SERVER stays the single authority on the geometry it stores. The arc
 * that ends up in the drawing is built by `_arc_through` in `edits.py` from these three points,
 * exactly as a plain 3-point arc is. Two implementations of "what arc is this" is precisely the
 * drift this codebase keeps finding.
 *
 * ⚠⚠ **EVERY ARC HERE IS COUNTER-CLOCKWISE FROM ITS START, WHICH IS AutoCAD'S RULE AND NOT AN
 * ARBITRARY ONE.** Ignore it and half of these givens draw the complementary arc — the right
 * radius, the right endpoints, the wrong 300° of it. The failure is invisible to any test that
 * checks the endpoints and obvious in a picture, which is how `_arc_through` came to carry the
 * same warning on the server side.
 */

/** ⚠ One `Pt`, exported from here and re-exported by `commands.ts`. Two structurally
 *  identical interfaces would type-check against each other and then quietly diverge the first
 *  time either grew a `z`. */
export interface Pt { x: number; y: number }

/** A given that needs a typed number, and what to call it. */
export interface GivenAsk {
  key: string;
  label: string;
  prompt: string;
  /** ⚠ An angle is a number a drafter types; a chord length or radius scales with the drawing,
   *  so its default comes from the picks rather than a constant. */
  def?: (pts: Pt[]) => number;
}

export interface GivenDef {
  /** What `do_draw` is asked to make. */
  kind: "ARC" | "CIRCLE" | "ELLIPSE";
  /** How many CLICKS, before any typed answer. */
  points: number;
  prompts: string[];
  ask?: GivenAsk;
  /** The canonical points, or a SENTENCE saying why not.
   *  ⚠ A string result is shown in the command line as-is — "a chord longer than the diameter
   *  has no arc" is something a drafter can act on; `null` is not. */
  solve: (pts: Pt[], n: number) => Pt[] | string;
}

const TAU = Math.PI * 2;
const d2r = (d: number) => (d * Math.PI) / 180;
const ang = (c: Pt, p: Pt) => Math.atan2(p.y - c.y, p.x - c.x);
const dist = (a: Pt, b: Pt) => Math.hypot(b.x - a.x, b.y - a.y);
const at = (c: Pt, r: number, a: number): Pt => ({ x: c.x + r * Math.cos(a), y: c.y + r * Math.sin(a) });

/** Three points on the arc that runs COUNTER-CLOCKWISE `sweep` radians from `a0`.
 *
 * ⚠⚠ **THE MIDDLE POINT IS HALF THE SWEEP ALONG, NOT THE MIDPOINT OF THE CHORD.** The server
 * picks between the two arcs through three points by asking which one CONTAINS the middle one —
 * so a middle point that is merely near the arc, or on the wrong half, silently selects the
 * complementary arc. This is the one line that decides which 300° a 300° arc is. */
function arcPts(c: Pt, r: number, a0: number, sweep: number): Pt[] | string {
  if (!(r > 1e-9)) return "those picks give an arc with no radius";
  if (!Number.isFinite(sweep) || Math.abs(sweep) < 1e-9) return "that is a zero-length arc";
  let s = sweep % TAU;
  if (s <= 0) s += TAU;
  return [at(c, r, a0), at(c, r, a0 + s / 2), at(c, r, a0 + s)];
}

/** The centre of the arc through a chord, given the perpendicular offset from its midpoint.
 *  ⚠ `side` +1 puts the centre to the LEFT of start→end, which is the side that makes the
 *  counter-clockwise sweep the MINOR arc. That is the convention every chord-based given here
 *  relies on, and flipping it is how a "Start, End, Radius" arc comes out inside out. */
function fromChord(s: Pt, e: Pt, h: number, side: number): Pt {
  const c = dist(s, e);
  const ux = (e.x - s.x) / c, uy = (e.y - s.y) / c;
  return { x: (s.x + e.x) / 2 - uy * h * side, y: (s.y + e.y) / 2 + ux * h * side };
}

/** Centre, start and end — the shape shared by four of the eleven arc givens. */
function fromCentreStart(c: Pt, s: Pt, endRay: Pt): Pt[] | string {
  const r = dist(c, s);
  return arcPts(c, r, ang(c, s), ang(c, endRay) - ang(c, s));
}

/** Centre, start, and an INCLUDED ANGLE in degrees. ⚠ Negative is clockwise, which AutoCAD
 *  accepts and a drafter uses to get the other way round without re-picking. */
function fromCentreAngle(c: Pt, s: Pt, deg: number): Pt[] | string {
  const r = dist(c, s);
  if (!Number.isFinite(deg) || Math.abs(deg) < 1e-9) return "an arc needs an angle";
  return arcPts(c, r, ang(c, s), d2r(deg));
}

/** Centre, start, and a CHORD LENGTH. ⚠ A negative length takes the MAJOR arc — AutoCAD's own
 *  sign convention, and the only way to ask for the long way round from this given. */
function fromCentreChord(c: Pt, s: Pt, len: number): Pt[] | string {
  const r = dist(c, s);
  const L = Math.abs(len);
  if (!(L > 1e-9)) return "an arc needs a chord length";
  if (L > 2 * r + 1e-9) return `a chord of ${L} is longer than the diameter ${(2 * r).toFixed(3)}`;
  const half = Math.asin(Math.min(1, L / (2 * r)));
  return arcPts(c, r, ang(c, s), len < 0 ? TAU - 2 * half : 2 * half);
}

export const CIRCLE_GIVENS: Record<string, GivenDef> = {
  cr: {
    kind: "CIRCLE", points: 2,
    // ⚠⚠ `Ttt` was BUILT and reachable only from the ribbon flyout — `tangency.ts` has held the
    // three-line solve since it was written. `Multiple` keeps the tool armed, which is what
    // BricsCAD's `Multiple circles` does and what a drafter setting out columns wants.
    // ⚠ `turn Arc into circle` is BricsCAD's sixth, and it is a CONVERSION rather than a way of
    // collecting points — `arc2circle` on the server replaces the arc with a circle on its own
    // centre and radius. Noun-verb, like TTR and TTT: select the arc, then say so.
    // ⚠ It abbreviates to A, which nothing else on this prompt uses. `matchKeyword` would refuse
    // it rather than guess if that ever stopped being true.
    prompts: ["Specify center point for circle or "
              + "[3P/2P/Ttr (tan tan radius)/Ttt (tan tan tan)/Multiple/"
              + "turn Arc into circle]:",
              "Specify radius of circle or [Diameter]:"],
    solve: (p) => (dist(p[0], p[1]) < 1e-9 ? "a circle needs a radius" : p),
  },
  cd: {
    kind: "CIRCLE", points: 1,
    prompts: ["Specify center point for circle:"],
    // ⚠⚠ **THE NUMBER IS A DIAMETER AND THE SERVER WANTS A RADIUS.** Sending the typed value
    // through as a rim distance draws every circle twice the size asked for — a mistake that
    // looks like a units problem and is not.
    ask: { key: "d", label: "Diameter", prompt: "Specify diameter of circle:" },
    solve: (p, n) => (n > 1e-9 ? [p[0], { x: p[0].x + n / 2, y: p[0].y }]
                               : "a circle needs a diameter"),
  },
  "2p": {
    kind: "CIRCLE", points: 2,
    prompts: ["Specify first end point of circle's diameter:",
              "Specify second end point of circle's diameter:"],
    // ⚠ The two clicks are the ends of a DIAMETER, not a centre and a rim.
    solve: (p) => (dist(p[0], p[1]) < 1e-9 ? "those two points are the same"
                                           : [{ x: (p[0].x + p[1].x) / 2,
                                                y: (p[0].y + p[1].y) / 2 }, p[0]]),
  },
  "3p": {
    kind: "CIRCLE", points: 3,
    prompts: ["Specify first point on circle:", "Specify second point on circle:",
              "Specify third point on circle:"],
    solve: (p) => {
      const c = circleThrough(p[0], p[1], p[2]);
      return c ? [{ x: c.x, y: c.y }, p[0]] : "those three points are in a line, so no circle "
                                              + "passes through them";
    },
  },
};

/** ⚠⚠ **BricsCAD V26 PRINTS `[Arc/Center]` ON ELLIPSE, AND THE DRAW MENU HAS PROMISED `Center`
 *  SINCE THE FLYOUT WAS WRITTEN — WITHOUT DRAWING ONE.** `draw.ellipse.center` mapped straight to
 *  the plain `ellipse` mode, so picking Center off the menu ran the AXIS-END gesture: the click
 *  meant as the middle landed on the rim, and the ellipse came out twice the size and offset by
 *  its own radius. Nothing failed and nothing said so — the WRONG-not-MISSING shape, and the one
 *  a drafter trusts until they measure it.
 *
 *  ⚠ `Arc` is not a given at all — it is the `ellipsearc` tool, which has been built and
 *  reachable only from a menu item since it was written. The keyword is its second door.
 */
export const ELLIPSE_GIVENS: Record<string, GivenDef> = {
  axis: {
    kind: "ELLIPSE", points: 3,
    prompts: ["Specify axis endpoint of ellipse or [Arc/Center]:",
              "Specify other endpoint of axis:",
              "Specify distance to other axis:"],
    solve: (p) => (dist(p[0], p[1]) < 1e-9 ? "an ellipse needs an axis with some length" : p),
  },
  c: {
    kind: "ELLIPSE", points: 3,
    prompts: ["Specify center of ellipse:",
              "Specify endpoint of axis:",
              "Specify distance to other axis:"],
    // ⚠⚠ **THE SERVER TAKES THE TWO ENDS OF ONE AXIS, NEVER A CENTRE.** So this given is solved
    // by REFLECTING the axis endpoint through the centre — the far end is `2C - A`. Sending the
    // centre through as if it were an axis end is exactly the defect above.
    // ⚠ The third pick is passed on UNCHANGED. Its distance is measured from the axis MIDPOINT,
    // which for this gesture is the centre already picked, so it means the same thing in both.
    solve: (p) => (dist(p[0], p[1]) < 1e-9 ? "an ellipse needs an axis with some length"
                   : [{ x: 2 * p[0].x - p[1].x, y: 2 * p[0].y - p[1].y }, p[1], p[2]]),
  },
};

export const ARC_GIVENS: Record<string, GivenDef> = {
  "3p": {
    kind: "ARC", points: 3,
    prompts: ["Specify start point of arc or [Center]:",
              "Specify second point of arc or [Center/End]:",
              "Specify end point of arc:"],
    solve: (p) => p,
  },
  // ⚠⚠ `sce` AND `cse` ARE THE SAME ARC FROM DIFFERENT PICK ORDERS, AND THAT IS WHY BOTH EXIST.
  // A drafter reaching for "Start, Center, End" and getting the centre-first gesture puts the
  // arc somewhere else and finds out later — which is exactly the note the greyed-out buttons
  // used to carry.
  sce: {
    kind: "ARC", points: 3,
    prompts: ["Specify start point of arc:", "Specify center point of arc:",
              // ⚠ AutoCAD's own offer at this step, and both solvers below already exist:
              // Angle is `sca` and chord Length is `scl`, each taking exactly the two points
              // already picked. The keyword was missing, not the code.
              "Specify end point of arc or [Angle/chord Length]:"],
    solve: (p) => fromCentreStart(p[1], p[0], p[2]),
  },
  cse: {
    kind: "ARC", points: 3,
    prompts: ["Specify center point of arc:", "Specify start point of arc:",
              // ⚠ Centre-first, so these route to `csa` and `csl` rather than `sca`/`scl` —
              // the same arcs from the other pick order. Mapping them to the start-first pair
              // would put the arc somewhere else, which is the note `sce` and `cse` carry.
              "Specify end point of arc or [Angle/chord Length]:"],
    solve: (p) => fromCentreStart(p[0], p[1], p[2]),
  },
  sca: {
    kind: "ARC", points: 2,
    prompts: ["Specify start point of arc:", "Specify center point of arc:"],
    ask: { key: "a", label: "Angle", prompt: "Specify included angle:", def: () => 90 },
    solve: (p, n) => fromCentreAngle(p[1], p[0], n),
  },
  csa: {
    kind: "ARC", points: 2,
    prompts: ["Specify center point of arc:", "Specify start point of arc:"],
    ask: { key: "a", label: "Angle", prompt: "Specify included angle:", def: () => 90 },
    solve: (p, n) => fromCentreAngle(p[0], p[1], n),
  },
  scl: {
    kind: "ARC", points: 2,
    prompts: ["Specify start point of arc:", "Specify center point of arc:"],
    ask: { key: "l", label: "Length", prompt: "Specify length of chord:",
           def: (p) => dist(p[0], p[1]) },
    solve: (p, n) => fromCentreChord(p[1], p[0], n),
  },
  csl: {
    kind: "ARC", points: 2,
    prompts: ["Specify center point of arc:", "Specify start point of arc:"],
    ask: { key: "l", label: "Length", prompt: "Specify length of chord:",
           def: (p) => dist(p[0], p[1]) },
    solve: (p, n) => fromCentreChord(p[0], p[1], n),
  },
  sea: {
    kind: "ARC", points: 2,
    prompts: ["Specify start point of arc:", "Specify end point of arc:"],
    ask: { key: "a", label: "Angle", prompt: "Specify included angle:", def: () => 90 },
    solve: (p, n) => {
      const c = dist(p[0], p[1]);
      if (c < 1e-9) return "those two points are the same";
      let a = n % 360;
      if (a <= 0) a += 360;
      if (Math.abs(a - 360) < 1e-9) return "a 360° arc is a circle — use CIRCLE";
      const r = c / (2 * Math.sin(d2r(a) / 2));
      // ⚠ Past 180° the centre crosses to the OTHER side of the chord. `cos(a/2)` goes negative
      // there and carries the flip on its own, which is why there is no branch here.
      const centre = fromChord(p[0], p[1], r * Math.cos(d2r(a) / 2), 1);
      return arcPts(centre, Math.abs(r), ang(centre, p[0]), d2r(a));
    },
  },
  ser: {
    kind: "ARC", points: 2,
    prompts: ["Specify start point of arc:", "Specify end point of arc:"],
    // ⚠ A NEGATIVE radius takes the major arc. AutoCAD's own convention, and without it this
    // given can only ever draw the short way round.
    ask: { key: "r", label: "Radius", prompt: "Specify radius of arc:",
           def: (p) => dist(p[0], p[1]) },
    solve: (p, n) => {
      const c = dist(p[0], p[1]);
      const R = Math.abs(n);
      if (c < 1e-9) return "those two points are the same";
      if (R < c / 2 - 1e-9) return `a radius of ${R} cannot reach across a chord of ${c.toFixed(3)}`;
      const h = Math.sqrt(Math.max(0, R * R - (c / 2) * (c / 2)));
      const centre = fromChord(p[0], p[1], h, n < 0 ? -1 : 1);
      const a0 = ang(centre, p[0]);
      return arcPts(centre, R, a0, ang(centre, p[1]) - a0);
    },
  },
  sed: {
    kind: "ARC", points: 3,
    prompts: ["Specify start point of arc:", "Specify end point of arc:",
              // ⚠ AutoCAD offers [Angle/Direction/Radius] after the end point. DIRECTION is
              // what this given already is, so the two on offer are the other two: `sea` and
              // `ser`, both of which take the start and end already picked.
              "Specify a point giving the tangent direction at the start "
              + "or [Angle/Radius]:"],
    // ⚠⚠ **THE ONE GIVEN THAT ANSWERS WITH A DIRECTION RATHER THAN A NUMBER**, which is why it
    // takes three clicks and no typed value. The third pick is not on the arc.
    solve: (p) => {
      const [s, e, d] = p;
      const dx = d.x - s.x, dy = d.y - s.y;
      const dn = Math.hypot(dx, dy);
      if (dn < 1e-9) return "the third point must be away from the start, to give a direction";
      if (dist(s, e) < 1e-9) return "the start and the end are the same point";
      // The centre is perpendicular to the tangent at the start, at the distance that also
      // reaches the end: solve |S + n·t − E| = |t|.
      const nx = -dy / dn, ny = dx / dn;
      const ex = s.x - e.x, ey = s.y - e.y;
      const den = 2 * (nx * ex + ny * ey);
      if (Math.abs(den) < 1e-12) return "that direction points straight at the end point";
      const t = -(ex * ex + ey * ey) / den;
      const centre = { x: s.x + nx * t, y: s.y + ny * t };
      const r = Math.abs(t);
      const a0 = ang(centre, s);
      // ⚠ Which WAY round. The counter-clockwise tangent at the start is the radius turned 90°;
      // if the drafter's direction disagrees with it, the arc goes the other way — and taking
      // the CCW sweep regardless is how this given draws the complement of what was asked for.
      const ccw = -(s.y - centre.y) * dx + (s.x - centre.x) * dy > 0;
      const sweep = ang(centre, e) - a0;
      return ccw ? arcPts(centre, r, a0, sweep)
                 : arcPts(centre, r, ang(centre, e), a0 - ang(centre, e));
    },
  },
};

/** The centre and radius of the circle through three points, or null if they are collinear.
 *  ⚠ Duplicated from `commands.ts` deliberately? No — imported there. Kept here because every
 *  other solver in this file is here and a caller should not have to know which file each
 *  given's arithmetic lives in. */
export function circleThrough(a: Pt, b: Pt, c: Pt): { x: number; y: number; r: number } | null {
  const d = 2 * (a.x * (b.y - c.y) + b.x * (c.y - a.y) + c.x * (a.y - b.y));
  if (Math.abs(d) < 1e-12) return null;
  const sa = a.x * a.x + a.y * a.y, sb = b.x * b.x + b.y * b.y, sc = c.x * c.x + c.y * c.y;
  const x = (sa * (b.y - c.y) + sb * (c.y - a.y) + sc * (a.y - b.y)) / d;
  const y = (sa * (c.x - b.x) + sb * (a.x - c.x) + sc * (b.x - a.x)) / d;
  return { x, y, r: Math.hypot(a.x - x, a.y - y) };
}

/** ⚠⚠ The two givens that pick OBJECTS rather than points, which is why they are not in
 *  `CIRCLE_GIVENS` with the rest. **They ARE built now** — `tangency.ts` holds the solve, and
 *  `Studio.setCircGiven` routes these two through the selection instead of through `solveGiven`.
 *  This set is what makes that fork, so it is still the one place that names them. */
export const TANGENT_GIVENS = new Set(["ttr", "ttt"]);

export function givenOf(mode: string, given: string): GivenDef | undefined {
  // ⚠ ELLIPSE has a given even with nothing chosen: `axis` IS the default gesture, and routing
  // it through the table is what lets the first prompt carry `[Arc/Center]` in one place.
  if (mode === "ellipse") return ELLIPSE_GIVENS[given] ?? ELLIPSE_GIVENS.axis;
  if (mode === "circle") return CIRCLE_GIVENS.cr;
  if (mode === "circv") return CIRCLE_GIVENS[given];
  if (mode === "arc" || mode === "arc3") return ARC_GIVENS["3p"];
  if (mode === "arcctr") return ARC_GIVENS.cse;
  if (mode === "arcv") return ARC_GIVENS[given];
  return undefined;
}
